import { DropdownItemContent, MenuItem, MenuItemTitle } from '@statsbomb/kitbag-components';
import { KeysToCamelCase, Search, SearchResult, UserNavOverrides } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { userNavOverridesAtom } from '@/atoms/queries/user';
import { FlagIcon } from '@/components/icons/FlagIcon';
import { classicEntityDashboardUrl } from '@/utils/classicLinks';
import { formatYearInfo } from '@/utils/date';
import { formatPlayerName } from '@/utils/player';
import { isNullish } from '@/utils/predicates';
import { buildSearchResultPath } from './buildSearchResultPath';

const SearchItem = ({
  item,
  onClick,
  userNavOverrides,
  pathname,
  search,
  language,
}: {
  item: SearchResult;
  onClick: () => void;
  userNavOverrides: KeysToCamelCase<UserNavOverrides>;
  pathname: string;
  search: string;
  language: string;
}) => {
  const { id, type, name, nickname, code, age, clubName, yearOfBirth, competitionName } = item;
  const displayName = formatPlayerName(type === 'player' ? nickname : null, name);

  const yearInfo = formatYearInfo(yearOfBirth, age);
  const playerContextItems = [clubName, yearInfo].filter(
    (contextItem): contextItem is string => !isNullish(contextItem),
  );
  const teamContextItems = competitionName ? [competitionName] : [];
  const contextItems = type === 'player' ? playerContextItems : teamContextItems;

  // Keep the user on their current sub-view (pitch vis, radar, etc.) when selecting another entity,
  // rather than always sending them to the dashboard. The classic IQ route is left as-is.
  const parachuteUrl = buildSearchResultPath({ pathname, search, language, type, id });
  const classicResultUrl = userNavOverrides[type] ? classicEntityDashboardUrl(type, id) : undefined;
  const url = classicResultUrl ?? parachuteUrl;

  return (
    <MenuItem
      component={Link}
      componentProps={{ to: url }}
      onClick={onClick}
      role="option"
      testId={`${type}-${id}`}
      isOpaque
      size="small"
      variant="default"
    >
      <DropdownItemContent
        context={contextItems}
        icon={<FlagIcon naturalDimensions countryCode={code} size="regular" />}
      >
        {displayName}
      </DropdownItemContent>
    </MenuItem>
  );
};

type SearchResultsProps = { searchResults: Search; onClick: () => void };

// The result rows without a container, for a field that provides its own results panel (Home's Kitbag
// SearchField). The top bar wraps them in SearchResults' positioned panel.
export const SearchResultsItems = ({ searchResults, onClick }: SearchResultsProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('entity');
  const { pathname, search } = useLocation();
  const userNavOverrides = useAtomValue(userNavOverridesAtom);

  return (
    <>
      {Object.entries(searchResults).map(([key, results]) => (
        <React.Fragment key={key}>
          <MenuItem role="presentation" size="regular" tabIndex={-1} testId={`${key}-results`}>
            <MenuItemTitle>
              {t(`${key}_other`)} ({results.length})
            </MenuItemTitle>
          </MenuItem>
          {results.map(result => (
            <SearchItem
              key={`${result.type}-${result.id}`}
              item={result}
              onClick={onClick}
              userNavOverrides={userNavOverrides}
              pathname={pathname}
              search={search}
              language={language}
            />
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

export const SearchResults = ({ searchResults, onClick, label }: SearchResultsProps & { label: string }) => (
  <div
    role="listbox"
    aria-label={label}
    className="absolute right-0 bg-canvas-tertiary-main md:min-w-96 md:!w-96 md:shadow-sb-regular md:mt-2 z-10 w-full max-h-[calc(100dvh-4rem)] overflow-y-auto"
  >
    <SearchResultsItems searchResults={searchResults} onClick={onClick} />
  </div>
);
